import React, { memo } from 'react'
import tw from 'twin.macro'
import { Tooltip, Typography } from '@mui/material'
import { Button } from 'src/components/shared/Button'
import { Chip } from 'src/components/shared/Chip'
import { semanticColors } from 'src/constants/semanticTokens'
import type { OAuthClient } from 'src/interfaces/oauth'
import { isOAuthClientActive } from 'src/utils/oauthClient'

const DIRTY_FORM_HINT = 'Save or discard your scope changes first.'

interface Props {
  client: OAuthClient
  // PUT /oauth/:id/client — `oauth_clients_manage`.
  canToggleStatus: boolean
  // POST /oauth/:id/revoke-tokens — `oauth_tokens_revoke`.
  canRevoke: boolean
  isScopeFormDirty: boolean
  isSaving: boolean
  isRevoking: boolean
  onToggleStatus(): void
  onOpenRevoke(): void
}

const OAuthStatusBarComponent = ({
  client,
  canToggleStatus,
  canRevoke,
  isScopeFormDirty,
  isSaving,
  isRevoking,
  onToggleStatus,
  onOpenRevoke,
}: Props) => {
  const isActive = isOAuthClientActive(client)

  return (
    <div
      data-testid="admin-customer-oauth-status-bar"
      css={tw`flex flex-row flex-wrap items-center gap-3 rounded-lg border border-solid border-shades-100 bg-shades-0 p-4`}
    >
      <Typography variant="h6" css={tw`text-shades-1000`}>
        API access
      </Typography>
      {isActive ? (
        <Chip
          text="Active"
          className={{ backgroundColor: semanticColors.feedback.successBg }}
          textClassName={{ color: semanticColors.feedback.successFg }}
          dataTestId="oauth-status-chip"
        />
      ) : (
        <Chip
          text="Disabled"
          className={tw`bg-shades-100`}
          textClassName={tw`text-shades-500`}
          dataTestId="oauth-status-chip"
        />
      )}
      {canToggleStatus || canRevoke ? (
        <div css={tw`ml-auto flex flex-row items-center gap-2`}>
          {/* Status-only: the page builds this payload from the client's persisted scopes, never
            from the form. While scope edits are unsaved the button is disabled so a status flip
            can never smuggle an unintended scope change with it. */}
          {canToggleStatus ? (
            <Tooltip title={isScopeFormDirty ? DIRTY_FORM_HINT : ''}>
              <span>
                <Button
                  size="small"
                  color="white"
                  loading={isSaving}
                  disabled={isScopeFormDirty || isSaving}
                  onClick={onToggleStatus}
                  data-testid="oauth-toggle-status-button"
                >
                  {isActive ? 'Disable' : 'Enable'}
                </Button>
              </span>
            </Tooltip>
          ) : null}
          {canRevoke ? (
            <Button
              size="small"
              color="red"
              className={tw`text-critical-500`}
              disabled={isRevoking}
              onClick={onOpenRevoke}
              data-testid="oauth-revoke-tokens-button"
            >
              Revoke all tokens
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export const OAuthStatusBar = memo(OAuthStatusBarComponent)
